// Copyright 2016 Sorama B.V.

import { autoinject, computedFrom, Disposable } from "aurelia-framework";
import { RecordingContainer, RecordingGroupContainer, RecordingGroupMetadata } from "datatypes/Containers";
import { As2LeakType, ILeakqResult } from "datatypes/FlukeAs2Implementation";
import { MetadataValuePair } from "datatypes/MetadataFields";
import { RecordingStep } from "datatypes/ScanStep";
import { PDDDischarge } from "datatypes/SoramaAs2Implementation";
import {
    CaptureDevice,
    FrontEnd,
    FrontEndConnector,
    InputFileProperties,
    Point3D,
    Point3f,
    ReorderProperties,
} from "datatypes/SoramaLocalApi";
import {
    As2ImportResult,
    DataPart,
    GenericMeasurement,
    ImageDTO,
    MeasurementDeviceInfo,
    MeasurementMediaObjectLink,
    MeasurementStorageInfo,
    NewCompatibilityMeasurement,
    NewImageDTO,
    NewMeasurement,
    NewMeasurementPart,
    NewMeasurementPartType,
    Range,
    SpecialMeasurementType,
    StorageInfo,
    TargetAndSourceRegionLinkProperties,
} from "datatypes/SoramaWebApi";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { StringExtensions } from "extensions/StringExtensions";
import { fixedEncodeURIComponent } from "extensions/UriExtensions";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";

import { URA } from "libraries/Math/URA";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { NewLinkedMediaObject } from "repositories/LinkedMediaObjectRepository";
import { Repository } from "repositories/Repository";
import { IStorageService } from "services/Storage/IStorageService";

export enum MeasurementField {
    selectedImage = "selectedImage",
    mutedSensors = "mutedSensors",
    metadata = "metadata",
    surveyName = "surveyName",
    name = "name",
    notes = "notes",
    distance = "distance",
}

export class MeasurementUpdateField {
    public readonly getUriComponent: (obj: unknown) => string;
    public readonly getBodyComponent: (obj: unknown) => unknown;

    private readonly _uriPath: string;
    public get uriPath(): string {
        return this._uriPath;
    }

    private readonly _field: MeasurementField;
    public get field(): MeasurementField {
        return this._field;
    }

    private constructor(
        field: MeasurementField,
        uriPath: string,
        getUriComponent?: (obj: unknown) => string,
        getBodyComponent?: (obj: unknown) => unknown
    ) {
        this._field = field;
        this._uriPath = uriPath;
        this.getUriComponent = !getUriComponent ? () => "" : getUriComponent;
        this.getBodyComponent = !getBodyComponent ? () => undefined : getBodyComponent;
    }

    public static readonly measurementName: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.name,
        "Name",
        (newName: string) => "/" + fixedEncodeURIComponent(newName)
    );
    public static readonly measurementNotes: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.notes,
        "Notes",
        () => "",
        (newNotes: string) => {
            return { notes: newNotes };
        }
    );
    public static readonly measurementDistance: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.distance,
        "Distance",
        (newDistance: number) => "/" + fixedEncodeURIComponent(newDistance.toString()) + "/"
    );
    public static readonly measurementMetadata: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.metadata,
        "Metadata",
        (x: MetadataValuePair) => "/" + fixedEncodeURIComponent(x.key) + "/" + fixedEncodeURIComponent(x.value)
    );
    public static readonly measurementSurveyName: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.surveyName,
        "SurveyName",
        (newSurveyName: string) => "/" + fixedEncodeURIComponent(newSurveyName)
    );
    public static readonly mutedSensors: MeasurementUpdateField = new MeasurementUpdateField(
        MeasurementField.mutedSensors,
        "MutedSensors",
        () => "",
        (x) => x
    );

    public static readonly measurementUpdateFields = [
        MeasurementUpdateField.measurementName,
        MeasurementUpdateField.measurementNotes,
        MeasurementUpdateField.measurementDistance,
        MeasurementUpdateField.measurementMetadata,
        MeasurementUpdateField.measurementSurveyName,
        MeasurementUpdateField.mutedSensors,
    ];
    public static fromString(value: string): MeasurementUpdateField {
        const measurementUpdateField = this.measurementUpdateFields.find(
            (x) => x.field.toLowerCase() === value.toLowerCase()
        );
        if (!measurementUpdateField) throw new Error(`Cannot update the measurement field ${value}`);
        return measurementUpdateField;
    }
}

@autoinject
export class MeasurementRepository extends Repository implements IAuthenticationHandler {
    private static stationaryHolographyMeasurementApi = "StationaryHolographyMeasurement";
    private static transientHolographyMeasurementApi = "TransientHolographyMeasurement";
    private static beamformingMeasurementApi = "BeamformingMeasurement";

    private static frontEndNodeName = "frontEnd";
    private static frontEndConnectorsNodeName = "frontEndConnectors";
    private static microphoneArrayIdsNodeName = "microphoneArrayIds";

    private static soundFileName = "sound";
    private static referenceChannelSoundFileName = "sound_reference_channel";
    private static videoFileName = "video";

    private static measurementSoundFileName = "step-{0}.wave";
    private static measurementVideoFileName = "video.mjpeg";
    private static arrayFileName = "array.xml";

    private webRequestHandler: IWebRequestHandler;
    private storageService: IStorageService;

    private measurementUpdateObservers: {
        [measurementId: string]: ((field: MeasurementField, measurement: GenericMeasurement) => void)[];
    } = {};
    private genericMeasurementUpdateObservers: ((field: MeasurementField, measurement: GenericMeasurement) => void)[] =
        [];

    private _measurementIds: string[] = [];
    @computedFrom("_measurementIds")
    public get measurementIds(): string[] {
        return this._measurementIds;
    }

    constructor(
        webRequestHandler: IWebRequestHandler,
        authentication: IAuthentication,
        storageService: IStorageService
    ) {
        super();
        this.webRequestHandler = webRequestHandler;
        this.storageService = storageService;

        authentication.addAuthenticationHandler(this);
    }

    //#region IAuthenticationHandler

    public onSignIn(userId: string): Promise<void> {
        return Promise.resolve();
    }

    public onSignOut(): Promise<void> {
        this._measurementIds.splice(0, this._measurementIds.length);

        return Promise.resolve();
    }

    //#endregion

    //#region Update callback

    /**
     * Register an observer for updates on the measurement with the provided id
     * @param measurementId Id of the measurement to observe
     * @param onUpdate Method to call when the measurement has been updated. The callback includes the field that was updated and a genericmeasurement object that is guaranteed to have the updated value set (other field are not necessarily up to date).
     */
    public measurementUpdateObserver(
        onUpdate: (field: MeasurementField, measurement: GenericMeasurement) => void,
        measurementId?: string
    ): Disposable {
        if (!measurementId) {
            this.genericMeasurementUpdateObservers.push(onUpdate);
            return {
                dispose: () => {
                    const index = this.genericMeasurementUpdateObservers.findIndex((x) => x === onUpdate);
                    if (index === -1) return;

                    this.genericMeasurementUpdateObservers.splice(index, 1);
                },
            };
        }

        if (!this.measurementUpdateObservers[measurementId]) {
            this.measurementUpdateObservers[measurementId] = [];
        }

        this.measurementUpdateObservers[measurementId].push(onUpdate);

        return {
            dispose: () => {
                const observers = this.measurementUpdateObservers[measurementId];
                if (!observers) return;

                const index = observers.findIndex((x) => x === onUpdate);
                if (index === -1) return;

                observers.splice(index, 1);
            },
        };
    }

    //#endregion

    //#region Get functions

    public unlockNoLogoExport(measurementId: string): Promise<void> {
        return this.webRequestHandler
            .createGetRequest<GenericMeasurement[]>(Api.measurement + "/" + measurementId + "/UnlockNoLogoExport")
            .send()
            .then(() => {});
    }

    public getMeasurementsForProject(projectId: string): Promise<GenericMeasurement[]> {
        return this.webRequestHandler
            .createGetRequest<GenericMeasurement[]>(Api.projects + "/" + projectId + "/Measurements")
            .send()
            .then((x) => {
                const measurements = x.content;
                // Make sure all the ids are in the id list
                for (const measurement of measurements) {
                    const index = this.measurementIds.findIndex((x) => x === measurement.id);
                    if (index === -1) {
                        this.measurementIds.push(measurement.id);
                    }
                }
                return measurements;
            })
            .catch((e) => this.onError(e));
    }

    public getMeasurement(measurementId: string): Promise<GenericMeasurement> {
        return this.webRequestHandler
            .createGetRequest<GenericMeasurement>(Api.measurement + "/" + measurementId)
            .send()
            .then((x) => {
                const measurement = x.content;

                // Make sure all the ids are in the id list
                const index = this.measurementIds.findIndex((x) => x === measurement.id);
                if (index === -1) {
                    this.measurementIds.push(measurement.id);
                }

                return measurement;
            })
            .catch((e) => this.onError(e));
    }

    public isMeasurementComplete(measurementId: string): Promise<StorageInfo> {
        return this.webRequestHandler
            .createPostRequest<MeasurementStorageInfo[]>(Api.measurement + "/Storage/Status", [measurementId])
            .send()
            .then((x) => x.content[0].storageInfo)
            .catch((e) => this.onError(e));
    }

    public getMeasurementStorageInfos(measurementIds: string[]): Promise<MeasurementStorageInfo[]> {
        if (measurementIds.length === 0) return Promise.resolve([]);
        return this.webRequestHandler
            .createPostRequest<MeasurementStorageInfo[]>(Api.measurement + "/Storage/Status", measurementIds)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getMeasurementDataParts(measurementId: string): Promise<DataPart[][]> {
        return this.webRequestHandler
            .createGetRequest<DataPart[][]>(Api.measurement + "/" + measurementId + "/Storage/Chunks")
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getComputedMeasurements(measurementId: string, processorType?: string): Promise<GenericMeasurement[]> {
        return this.webRequestHandler
            .createGetRequest<GenericMeasurement[]>(
                Api.computedMeasurements +
                    "/" +
                    measurementId +
                    "/Computed" +
                    (processorType ? "?type=" + processorType : "")
            )
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getPDDDischarges(measurementId: string): Promise<PDDDischarge[]> {
        return this.webRequestHandler
            .createGetRequest<PDDDischarge[]>(Api.measurement + "/" + measurementId + "/Discharges")
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getLeakRate(measurementId: string, leakType: As2LeakType): Promise<ILeakqResult> {
        return this.webRequestHandler
            .createGetRequest<ILeakqResult>(Api.measurement + "/" + measurementId + "/Leakrate/" + leakType)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getSorFile(measurementId: string): Promise<Blob> {
        return this.webRequestHandler
            .createGetRequest<Blob>(Api.measurement + "/" + measurementId + "/Sor")
            .withBinaryResponse()
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    public getSorFilesZip(measurementIds: string[], name?: string): Promise<Blob> {
        if (measurementIds.length === 0) throw Error("No measurements selected");
        return this.webRequestHandler
            .createGetRequest<unknown>(
                Api.measurement +
                    "/Sor/Zip?" +
                    measurementIds.map((x) => "measurementId=" + x).join("&") +
                    (name ? "&name=" + encodeURIComponent(name) : "")
            )
            .withBinaryResponse()
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    //#endregion

    //#region Update/Set functions

    public updateMeasurement(measurement: GenericMeasurement, property: MeasurementUpdateField, value: unknown) {
        return this.webRequestHandler
            .createPutRequest(
                Api.measurement + "/" + measurement.id + "/" + property.uriPath + property.getUriComponent(value),
                property.getBodyComponent(value)
            )
            .send()
            .then(() => {
                measurement[property.field.toString()] = value;

                this.genericMeasurementUpdateObservers.forEach((x) => x(property.field, measurement));

                const observers = this.measurementUpdateObservers[measurement.id];
                if (!observers || observers.length === 0) return;

                observers.forEach((x) => x(property.field, measurement));
            });
    }

    public updateSelectedImage(
        measurement: GenericMeasurement,
        imageId: string,
        properties: TargetAndSourceRegionLinkProperties
    ) {
        return this.webRequestHandler
            .createPutRequest<MeasurementMediaObjectLink>(
                Api.measurement + "/" + measurement.id + "/Image/" + imageId,
                properties
            )
            .send()
            .then((response) => {
                // Update the link manually so we dont need to reload the measurement from the server
                measurement.selectedImage = imageId;
                measurement.links = measurement.links ? measurement.links : [];

                // Check if there already was a link
                const existingLinkIdx = measurement.links.findIndex((x) => x.mediaObjectId === imageId);
                if (existingLinkIdx !== -1) {
                    measurement.links.splice(existingLinkIdx, 1);
                }
                measurement.links.push(response.content);
                this.genericMeasurementUpdateObservers.forEach((x) => x(MeasurementField.selectedImage, measurement));

                const observers = this.measurementUpdateObservers[measurement.id];
                if (!observers || observers.length === 0) return;

                observers.forEach((x) => x(MeasurementField.selectedImage, measurement));
            });
    }

    //#endregion

    //#region Create functions

    /**
     * Checks if the data in the NewCompatibilityMeasurement can be used for creating a Stationary Holography Measurement
     * @param newMeasurement NewCompatibilityMeasurement object containing information for measurement.
     * @deprecated This check can now be found in MeasurementKinds.ts
     */
    public isStationaryHolographyCompatible(newMeasurement: NewCompatibilityMeasurement): Promise<boolean> {
        return this.webRequestHandler
            .createPostRequest<boolean>(Api.measurement + "/IsStationaryHolographyCompatible", newMeasurement)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    /**
     * Create a new Stationary Holography Measurement for the RecordingGroup in the given container.
     * @param newMeasurement NewCompatibilityMeasurement object containing information for measurement.
     * @param recordingGroupId Id of the RecordingGroup storage that contains the measurement data. NOTE: Audio data must be present.
     * @param projectId Id of the Project to add the measurement to.
     */
    public createStationaryHolographyMeasurement(
        newMeasurement: NewCompatibilityMeasurement,
        recordingGroupId: string,
        projectId: string
    ): Promise<string> {
        let recordingGroupMetadata: RecordingGroupMetadata;

        return this.storageService
            .load(recordingGroupId)
            .then((container: RecordingGroupContainer) => {
                recordingGroupMetadata = container.metadata;

                // Check if audio data is present
                return Promise.all(
                    recordingGroupMetadata.steps.map((step) => {
                        if (!step.recordingId)
                            throw new Error("Measurement is not complete: some steps don't have a recording.");

                        return this.storageService.getFiles(step.recordingId).then((files) => {
                            if (files.every((x) => x !== MeasurementRepository.soundFileName))
                                throw new Error(
                                    "Can not create a Measurement because there is no audio data present for recording " +
                                        step.recordingId
                                );
                        });
                    })
                );
            })
            .then(() => {
                return this.createMeasurement(
                    MeasurementRepository.stationaryHolographyMeasurementApi,
                    newMeasurement,
                    projectId,
                    recordingGroupMetadata.steps
                );
            });
    }

    /**
     * Checks if the data in the NewCompatibilityMeasurement can be used for creating a Transient Holography Measurement
     * @param newMeasurement NewCompatibilityMeasurement object containing information for measurement.
     * @deprecated This check can now be found in MeasurementKinds.ts
     */
    public isTransientHolographyCompatible(newMeasurement: NewCompatibilityMeasurement): Promise<boolean> {
        return this.webRequestHandler
            .createPostRequest<boolean>(Api.measurement + "/IsTransientHolographyCompatible", newMeasurement)
            .send()
            .then((x) => x.content)
            .catch((e) => this.onError(e));
    }

    /**
     * Create a new Transient Holography Measurement for the RecordingGroup in the given container.
     * @param newMeasurement NewCompatibilityMeasurement object containing information for measurement.
     * @param recordingGroupId Id of the RecordingGroup storage that contains the measurement data. NOTE: Audio data must be present.
     * @param projectId Id of the Project to add the measurement to.
     */
    public createTransientHolographyMeasurement(
        newMeasurement: NewCompatibilityMeasurement,
        recordingGroupId: string,
        projectId: string
    ): Promise<string> {
        let recordingGroupMetadata: RecordingGroupMetadata;

        return this.storageService
            .load(recordingGroupId)
            .then((container: RecordingGroupContainer) => {
                recordingGroupMetadata = container.metadata;

                // Check if audio data is present
                if (recordingGroupMetadata.steps.length != 1)
                    throw new Error(
                        "Measurement does not have exactly 1 step. It is not possible to create a Transient Holography Measurement from this."
                    );

                const step = recordingGroupMetadata.steps[0];

                if (!step.recordingId) throw new Error("Measurement is not complete: no recording is present.");

                return this.storageService.getFiles(step.recordingId).then((files) => {
                    if (files.every((x) => x !== MeasurementRepository.soundFileName))
                        throw new Error(
                            "Can not create a Measurement because there is no audio data present for recording " +
                                step.recordingId
                        );
                });
            })
            .then(() => {
                return this.createMeasurement(
                    MeasurementRepository.transientHolographyMeasurementApi,
                    newMeasurement,
                    projectId,
                    recordingGroupMetadata.steps
                );
            });
    }

    /**
     * Create a new Beamforming Measurement for the RecordingGroup in the given container.
     * @param newMeasurement NewCompatibilityMeasurement object containing information for measurement.
     * @param recordingGroupId Id of the RecordingGroup that contains the measurement data. NOTE: Audio and Video data must be present.
     * @param projectId Id of the Project to add the measurement to.
     */
    public createBeamformingMeasurement(
        newMeasurement: NewCompatibilityMeasurement,
        recordingGroupId: string,
        projectId: string
    ): Promise<string> {
        // First check if there is data available on disk
        let recordingGroupMetadata: RecordingGroupMetadata;
        let recordingId: string;
        let arraySequence = "";

        return this.storageService
            .load(recordingGroupId)
            .then((container: RecordingGroupContainer) => {
                recordingGroupMetadata = container.metadata;

                // Check if data is present
                if (recordingGroupMetadata.steps.length != 1)
                    throw new Error(
                        "Measurement does not have exactly 1 step. It is not possible to create a Beamforming Measurement from this."
                    );

                const step = recordingGroupMetadata.steps[0];

                if (!step.recordingId) throw new Error("Measurement is not complete: no recording is present.");

                recordingId = step.recordingId;

                return this.storageService.getFiles(recordingId);
            })
            .then((files) => {
                if (files.every((x) => x !== MeasurementRepository.soundFileName))
                    throw new Error(
                        "Can not create a Measurement because there is no audio data present for recording " +
                            recordingId
                    );

                if (files.every((x) => x !== MeasurementRepository.videoFileName))
                    throw new Error(
                        "Can not create a Measurement because there is no video data present for recording " +
                            recordingId
                    );

                return this.storageService.load(recordingId);
            })
            .then((container) => {
                // Generate array id sequence file
                const frontEnd =
                    container.metadata.files[MeasurementRepository.soundFileName][
                        MeasurementRepository.frontEndNodeName
                    ];
                if (!frontEnd)
                    return Promise.reject<string>(
                        "Cannot create measurement because of invalid recording metadata. Missing 'frontEnd' property."
                    );

                const connectors = <unknown[]>frontEnd[MeasurementRepository.frontEndConnectorsNodeName];
                if (!connectors || !connectors.length)
                    return Promise.reject<string>(
                        "Cannot create measurement because of invalid recording metadata. Missing 'connectors' property."
                    );

                for (let i = 0; i < connectors.length; i++) {
                    const micIds = connectors[i][MeasurementRepository.microphoneArrayIdsNodeName];
                    if (!micIds || !micIds.length)
                        return Promise.reject<string>(
                            "Cannot create measurement because of invalid recording metadata. Missing or empty 'micIds' property for connector " +
                                i +
                                "."
                        );

                    for (let j = 0; j < micIds.length; j++) {
                        arraySequence = arraySequence + micIds[j];
                        if (j != micIds.length - 1) arraySequence = arraySequence + "\r\n";
                    }

                    if (i != connectors.length - 1) arraySequence = arraySequence + "\r\n";
                }

                return this.createMeasurement(
                    MeasurementRepository.beamformingMeasurementApi,
                    newMeasurement,
                    projectId,
                    recordingGroupMetadata.steps
                );
            })
            .then((measurementId) => {
                // Copy video data and create an array sequence file
                return Promise.all([
                    this.storageService.toMeasurementStorage(
                        recordingId,
                        measurementId,
                        MeasurementRepository.videoFileName,
                        MeasurementRepository.measurementVideoFileName
                    ),
                    this.storageService.createMeasurementStorageTextFile(
                        measurementId,
                        MeasurementRepository.arrayFileName,
                        encodeURI(arraySequence)
                    ),
                ]).then(() => measurementId);
            });
    }

    /**
     * Create a new Generic Measurement (new style) frot he recording group in the given container.
     * @param recordingGroupId Id of the recording group to create a measurement from.
     * @param projectId Id of the project to add the measurement to.
     */
    public createGenericMeasurement(recordingGroupId: string, projectId: string): Promise<string> {
        // Load recordinggroup
        return this.storageService
            .load(recordingGroupId)
            .then((container: RecordingGroupContainer) => {
                if (container.metadata.steps.some((x) => !x.recordingId)) throw new Error("Measurement is incomplete");

                // Get all steps
                return Promise.all(container.metadata.steps.map((x) => this.storageService.load(x.recordingId))).then(
                    (recordingContainers: RecordingContainer[]) => {
                        if (recordingContainers.length === 0)
                            throw new Error("Measurement does not contain any measurement data");

                        const partPromises: Promise<NewMeasurementPart>[] = [];

                        // Make sure all recordings are the same length and create measurement parts
                        let duration: number = recordingContainers[0].metadata.files.sound.duration;
                        let start: Date = this.asDate(recordingContainers[0].created);
                        let maxY: number = undefined;
                        let uraProps = undefined;

                        for (let i = 0; i < recordingContainers.length; i++) {
                            const recordingContainer = recordingContainers[i];
                            const step = container.metadata.steps[i];

                            // Compute location of the measurement
                            if (step.position == undefined) {
                                if (recordingContainers.length > 1) {
                                    if (!uraProps || !maxY) {
                                        // Determine ura
                                        const micPositions = this.getSensorPositionsFromRecording(
                                            recordingContainer,
                                            NewMeasurementPartType.Audio
                                        );
                                        uraProps = URA.getUraProperties3D(
                                            micPositions.map((x) => <Point3D>{ x: x.X, y: x.Y, z: x.Z })
                                        );

                                        if (!uraProps.isUra) {
                                            throw new Error(
                                                "Measurement is a scanning measurement but is not done using an URA"
                                            );
                                        }

                                        if (maxY === undefined) {
                                            maxY = -Number.MAX_SAFE_INTEGER;
                                            for (let i = 0; i < container.metadata.steps.length; i++) {
                                                maxY = Math.max(maxY, container.metadata.steps[i].stepLocation.Y);
                                            }
                                        }
                                    }

                                    step.position = <Point3f>{
                                        X: step.stepLocation.X * uraProps.width,
                                        Y: (maxY - step.stepLocation.Y) * uraProps.height,
                                        Z: 0,
                                    };
                                } else {
                                    step.position = <Point3f>{
                                        X: 0,
                                        Y: 0,
                                        Z: 0,
                                    };
                                }
                            }

                            if (duration !== recordingContainer.metadata.files.sound.duration)
                                throw new Error("Measurement dataparts are not of equal duration");

                            if (this.asDate(recordingContainer.created) < start) {
                                start = recordingContainer.created;
                            }

                            for (const file of recordingContainer.contents) {
                                const partType = this.getNewMeasurementPartType(file);
                                if (partType === undefined) continue;

                                // Get hashes
                                partPromises.push(
                                    this.storageService.getMD5Hash(recordingContainer.id, file).then((hashSet) => {
                                        return <NewMeasurementPart>{
                                            position: container.metadata.steps[i].position,
                                            type: partType,
                                            dataHash: hashSet.hash,
                                            dataSize: hashSet.size,
                                            dataContainer: recordingContainer.id,
                                            device: <MeasurementDeviceInfo>{
                                                hardwareModuleIds:
                                                    this.getHardwareModulesFromRecording(recordingContainer),
                                                sensorPositions: this.getSensorPositionsFromRecording(
                                                    recordingContainer,
                                                    partType
                                                ),
                                                sampleRate:
                                                    partType === NewMeasurementPartType.Audio
                                                        ? this.getSampleRateFromRecording(recordingContainer)
                                                        : 25,
                                            },
                                        };
                                    })
                                );
                            }
                        }

                        return Promise.all(partPromises)
                            .catch((e) => {
                                throw new Error(
                                    "An error occurred why trying to read the measurement data" +
                                        (e.message ? ": " + e.message : "")
                                );
                            })
                            .then((parts) => {
                                duration = duration / 1000;

                                // Create new measurement object
                                const measurement = <NewMeasurement>{
                                    name: container.metadata.name,
                                    duration: duration,
                                    start: start,
                                    parts: parts,
                                    metadata: {},
                                };

                                for (const metadataPair of container.metadata.metadata) {
                                    measurement.metadata[metadataPair.key] = metadataPair.value;
                                }

                                // Set optional parameters seperately so they dont become 'null'
                                if (container.metadata.notes) measurement.notes = container.metadata.notes;

                                if (container.metadata.distance) measurement.distance = container.metadata.distance;

                                // Post measurement to webapi
                                return this.webRequestHandler
                                    .createPostRequest<GenericMeasurement>(
                                        Api.measurement + "?projectId=" + projectId,
                                        measurement
                                    )
                                    .send()
                                    .then((x) => {
                                        const id = x.content.id;

                                        // Set project id and measurement id in the storage of the container
                                        const measurementIds = container.metadata.measurementIds
                                            ? container.metadata.measurementIds
                                            : <string[]>[];
                                        measurementIds.push(id);

                                        const projectIds = container.metadata.projectIds
                                            ? container.metadata.projectIds
                                            : <string[]>[];
                                        projectIds.push(projectId);

                                        return Promise.all([
                                            this.storageService.updateMetadata(
                                                container.id,
                                                "measurementIds",
                                                measurementIds
                                            ),
                                            this.storageService.updateMetadata(container.id, "projectIds", projectIds),
                                        ]).then(() => id);
                                    });
                            })
                            .catch((e) => {
                                throw new Error(e.message);
                            });
                    }
                );
            })
            .then((x) => {
                // Make sure the measurements list is up to date
                this.measurementIds.push(x);

                return x;
            });
    }

    public createBeamformProbeGenericMeasurement(
        name: string,
        measurementId: string,
        properties: { targetPoint: Point3D }
    ): Promise<GenericMeasurement> {
        return this.webRequestHandler
            .createPostRequest<GenericMeasurement>(
                Api.computedMeasurements +
                    "/" +
                    measurementId +
                    "/Computed/BeamformProbe?name=" +
                    encodeURIComponent(name),
                properties
            )
            .send()
            .then((x) => {
                const measurement = x.content;

                // Make sure all the ids are in the id list
                const index = this.measurementIds.findIndex((x) => x === measurement.id);
                if (index === -1) {
                    this.measurementIds.push(measurement.id);
                }

                return measurement;
            });
    }

    public createTimeRangeGenericMeasurement(
        name: string,
        measurementId: string,
        properties: { timeRange: Range }
    ): Promise<GenericMeasurement> {
        return this.webRequestHandler
            .createPostRequest<GenericMeasurement>(
                Api.computedMeasurements + "/" + measurementId + "/Computed/TimeRange?name=" + encodeURIComponent(name),
                properties
            )
            .send()
            .then((x) => {
                const measurement = x.content;

                // Make sure all the ids are in the id list
                const index = this.measurementIds.findIndex((x) => x === measurement.id);
                if (index === -1) {
                    this.measurementIds.push(measurement.id);
                }

                return measurement;
            });
    }

    /**
     * Allocates cloud storage for the measurement with the provided id
     * @param measurementId Id of the measurement to create the storage for
     */
    public allocateStorageForMeasurement(measurementId: string): Promise<void> {
        return this.webRequestHandler
            .createPostRequest(Api.measurement + "/" + measurementId + "/Storage")
            .send()
            .then(() => {});
    }

    public uploadMeasurementPartChunk(
        measurementId: string,
        partIndex: number,
        chunkId: number,
        data: Blob
    ): Promise<DataPart[]> {
        const request = this.webRequestHandler
            .createPostRequest<
                DataPart[]
            >(Api.measurement + "/" + measurementId + "/Storage/" + partIndex + "/" + chunkId, data)
            .withBinaryContent();

        return request.send().then((x) => x.content);
    }

    public uploadMeasurement(measurementId: string, data: Blob): Promise<void> {
        const request = this.webRequestHandler
            .createPostRequest(Api.measurement + "/" + measurementId + "/Storage/", data)
            .withBinaryContent();

        return request.send().then(() => {});
    }

    //#endregion

    //#region Delete functions

    /**
     * Deletes a measurement, this function requires the delete permission on measurements. Computed measurements may also be deleted using this method.
     * @param measurementId The id of the measurement to delete.
     */
    public deleteMeasurement(measurementId: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest<void>(Api.measurement + "/" + measurementId)
            .send()
            .then(() => {
                // Update measurement list
                const index = this.measurementIds.findIndex((x) => x === measurementId);
                if (index !== -1) {
                    this.measurementIds.splice(index, 1);
                }
            });
    }

    /**
     * Deletes a computed measurement, this function does not require the delete permission on measurements. This function does require update permission on the parent measurement.
     * @param measurementId The id of the computed measurement to delete.
     */
    public deleteComputedMeasurement(measurementId: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest<void>(Api.computedMeasurements + "/" + measurementId + "/Computed")
            .send()
            .then((x) => {
                // Update measurement list
                const index = this.measurementIds.findIndex((x) => x === measurementId);
                if (index !== -1) {
                    this.measurementIds.splice(index, 1);
                }
            });
    }

    public deleteMeasurementStorage(measurementId: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest<void>(Api.measurement + "/" + measurementId + "/Storage")
            .send()
            .then(() => {
                // Update measurement list
                const index = this.measurementIds.findIndex((x) => x === measurementId);
                this.measurementIds.splice(index, 1);
            });
    }

    /**
     * Delete a metadata entry from a measurement. This function does require update permission.
     * @param measurementId The id of the measurement.
     * @param metadataKey The metadata entry matching the key to be deleted from the metadata array.
     */
    public deleteMeasurementMetadata(measurementId: string, metadataKey: string): Promise<void> {
        return this.webRequestHandler
            .createDeleteRequest<void>(Api.measurement + "/" + measurementId + "/Metadata/" + metadataKey)
            .send()
            .then(() => {});
    }

    public deleteSelectedImage(measurement: GenericMeasurement) {
        return this.webRequestHandler
            .createDeleteRequest<void>(Api.measurement + "/" + measurement.id + "/Image")
            .send()
            .then(() => {
                measurement[MeasurementField.selectedImage] = undefined;

                this.genericMeasurementUpdateObservers.forEach((x) => x(MeasurementField.selectedImage, measurement));

                const observers = this.measurementUpdateObservers[measurement.id];
                if (!observers || observers.length === 0) return;

                observers.forEach((x) => x(MeasurementField.selectedImage, measurement));
            });
    }

    public async cloneMeasurement(measurementId: string, projectId: string): Promise<string> {
        return this.webRequestHandler
            .createPostRequest<GenericMeasurement>(
                Api.measurement + "/Clone/" + measurementId + "?projectId=" + projectId + "&returnExisting=true"
            )
            .send()
            .then((x) => {
                return (x.content as GenericMeasurement).id;
            });
    }
    //#endregion

    //#region Helpers
    private createMeasurement(
        api: string,
        newMeasurement: NewCompatibilityMeasurement,
        projectId: string,
        steps: RecordingStep[]
    ): Promise<string> {
        // Data is available, post the measurement to the server
        return this.webRequestHandler
            .createPostRequest<string>(Api.measurement + "/" + api + "?projectId=" + projectId, newMeasurement)
            .send()
            .then((x) => {
                // Measurement has been created on the server, copy all necessary data locally
                const measurementId = x.content;

                // Check if there are broken step indices
                let brokenStepIndices = false;
                for (const step0 of steps) {
                    for (const step1 of steps) {
                        if (step1 === step0) continue;

                        if (step1.stepIndex === step0.stepIndex) {
                            brokenStepIndices = true;
                            break;
                        }
                    }

                    if (brokenStepIndices) break;
                }

                return Promise.all(
                    steps.map((step, i) => {
                        // Convert step number to format with 4 0's eg step-0000.wave, step-0013.wave etc. since the old format expects 4 numbers
                        let fileNr = brokenStepIndices ? i.toString() : step.stepIndex.toString();
                        while (fileNr.length < 4) fileNr = "0" + fileNr; // Javascript is gay, C# Rulez

                        const measurementSoundFileName = StringExtensions.format(
                            MeasurementRepository.measurementSoundFileName,
                            fileNr
                        );

                        if (steps.length > 1) {
                            // Merge the array file and reference file to be compatible with the old format
                            // (in which the reference channel is assumed to be always present and the last channel of every step file for scanning measurements)
                            // and copy it to the measurement folder
                            const props = <ReorderProperties>{
                                outputFileName: measurementSoundFileName,
                                inputFiles: [
                                    <InputFileProperties>{
                                        id: step.recordingId,
                                        filename: MeasurementRepository.soundFileName,
                                        takeBytesPattern: [4 * newMeasurement.microphoneMask.filter((x) => x).length],
                                        skipBytesPattern: [0],
                                    },
                                    <InputFileProperties>{
                                        id: step.recordingId,
                                        filename: MeasurementRepository.referenceChannelSoundFileName,
                                        takeBytesPattern: [4 * 1],
                                        skipBytesPattern: [0],
                                    },
                                ],
                            };

                            return this.storageService.toMeasurementStorageReordered(measurementId, props);
                        }

                        // Copy the recording file straight to the measurement folder
                        return this.storageService.toMeasurementStorage(
                            step.recordingId,
                            measurementId,
                            MeasurementRepository.soundFileName,
                            measurementSoundFileName
                        );
                    })
                ).then(() => measurementId);
            });
    }

    private getHardwareModulesFromRecording(recording: RecordingContainer): string[] {
        let ids: string[] = [];
        // Check if the recording contains information about the microphone array rack.
        const microphoneArrayRacks = ObjectExtensions.safeGetOptionalPath(
            recording.metadata,
            "files",
            "sound",
            "microphoneArrayRacks"
        );
        if (microphoneArrayRacks) {
            // Get rack ids
            const rackIds = Object.getOwnPropertyNames(microphoneArrayRacks);
            ids = ids.concat(rackIds);
        }

        // No ids yet, try to get them from the frontend system
        const frontEndConnectors = ObjectExtensions.safeGetOptionalPath(
            recording.metadata,
            "files",
            "sound",
            "frontEnd",
            "frontEndConnectors"
        );

        if (frontEndConnectors) {
            for (const connector of frontEndConnectors as FrontEndConnector[]) {
                ids.push(connector.id);
                // Add microphone array ids
                ids = ids.concat(connector.microphoneArrayIds);
            }
        }

        // Get video part stuff
        const capDevice: CaptureDevice = ObjectExtensions.safeGetOptionalPath(
            recording.metadata,
            "files",
            "video",
            "captureDevice"
        ) as CaptureDevice;
        if (!capDevice) return ids;

        let idString = "V" + capDevice.vendorId + "/" + "P" + capDevice.productId;
        if (capDevice.position) {
            idString += "/X" + capDevice.position.X + "/Y" + capDevice.position.Y + "/Z" + capDevice.position.Z;
        }

        ids.push(idString);

        return ids;
    }

    private getSensorPositionsFromRecording(
        recording: RecordingContainer,
        partType: NewMeasurementPartType
    ): Point3f[] {
        if (partType === NewMeasurementPartType.Audio) {
            // First get all front end channels
            const positions = <Point3f[]>recording.metadata.files.sound.frontEnd.microphonePositions;

            // Mask with the channelselection
            const channelMask = <boolean[]>recording.metadata.files.sound.channelSelection;

            // Order channels if required
            const ordering = <number[]>recording.metadata.files.sound.channelOrdering;

            if (ordering) {
                // Order channels according to the mapping
                return ordering.map((i) => {
                    if (i >= positions.length || i < 0) {
                        throw new Error("Incorrect sensor position data for measurement");
                    }

                    return positions[i];
                });
            } else if (channelMask) {
                return positions.filter((x, i) => channelMask[i]);
            } else {
                return positions;
            }
        }

        if (partType === NewMeasurementPartType.Video) {
            const capDevicePosition: Point3f = ObjectExtensions.safeGetOptionalPath(
                recording.metadata,
                "files",
                "video",
                "captureDevice",
                "position"
            ) as Point3f;
            return capDevicePosition ? [capDevicePosition] : [];
        }
    }

    private getSampleRateFromRecording(recording: RecordingContainer): number {
        // Check if the recording contains a metadata samplerate field
        if (recording.metadata.sampleRate) return recording.metadata.sampleRate;

        const frontEnd = <FrontEnd>recording.metadata.files.sound.frontEnd;

        // Get sample rate from supported samplerate of the front ends
        if (frontEnd.supportedSampleRates.length === 1) return frontEnd.supportedSampleRates[0];
        else if (frontEnd.supportedSampleRates.some((x) => x === 46875)) return 46875;
        else throw new Error("Unsupported sample rate for device");
    }

    private asDate(date: Date | string): Date {
        if (typeof date === "string") return new Date(<string>date);

        return <Date>date;
    }

    private getNewMeasurementPartType(fileType: string): NewMeasurementPartType {
        let partType: NewMeasurementPartType;
        if (fileType.toLowerCase() === "sound") partType = NewMeasurementPartType.Audio;
        else if (fileType.toLowerCase() === "video") partType = NewMeasurementPartType.Video;

        return partType;
    }

    public registerLocallySavedSorFileInCloud(
        metadata: GenericMeasurement,
        projectId: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        // Project id is unknown for the AC, so we fill it in
        metadata.projectId = projectId;

        const call = this.webRequestHandler.createPostRequest<GenericMeasurement>(
            Api.sorMetadataFromFile + "?projectId=" + projectId,
            metadata
        );
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call.send().then((x) => x.content);
    }

    public uploadSorMeasurementToCloud(
        data: unknown,
        projectId: string,
        name: string,
        fileType: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const call = this.webRequestHandler.createPostRequest<GenericMeasurement>(
            Api.sorMeasurementsFromFile +
                "?projectId=" +
                projectId +
                "&fileType=" +
                fileType +
                "&measurementName=" +
                name,
            data
        );
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call.send().then((x) => x.content);
    }

    public uploadSorxMeasurementToCloud(
        data: unknown,
        projectId: string,
        name: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const call = this.webRequestHandler
            .createPostRequest<GenericMeasurement>(
                Api.sorxMeasurementsFromFile + "?projectId=" + projectId + "&measurementName=" + name,
                data
            )
            .withBinaryContent();
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call.send().then((x) => x.content);
    }

    // AS2 files that are being uploaded get no option to be imported locally first and are sent to the cloud instantly
    public uploadAs2MeasurementToCloud(
        data: unknown,
        projectId: string,
        name: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<As2ImportResult> {
        const call = this.webRequestHandler.createPostRequest(
            Api.as2MeasurementsFromFile + "?projectId=" + projectId + "&measurementName=" + name,
            data
        );
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call.send().then((x) => x.content);
    }

    public createCAMiV64Image(
        newImage: NewImageDTO,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<NewLinkedMediaObject<ImageDTO>> {
        const call = this.webRequestHandler.createPostRequest<ImageDTO>(Api.mediaImages, newImage);
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call
            .send()
            .then((x) => {
                const mediaObjectRes = {
                    mediaObject: x.content,
                    added: x.statusCode != 303,
                };
                return mediaObjectRes;
            })
            .catch((e) => this.onError(e));
    }

    public async createSpecialMeasurement(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any,
        projectId: string,
        progressCallback?: (progress: ProgressIndicator) => void
    ): Promise<GenericMeasurement> {
        const type = MeasurementRepository.getSpecialMeasurementTypeFromData(data);

        const newMeasurement: NewMeasurement = {
            name: data.name || `${type} measurement`,
            notes: data.notes || "",
            parts: [],
            duration: 0,
            distance: 0,
            start: new Date(),
            specialMeasurementType: type,
            specialMeasurementInfo: data,
        };

        const call = this.webRequestHandler.createPostRequest<GenericMeasurement>(
            Api.specialMeasurementsFromFile + "?projectId=" + projectId,
            newMeasurement
        );
        if (progressCallback != undefined) {
            call.withUploadProgress(progressCallback).withDownloadProgress(progressCallback);
        }

        return call.send().then((x) => x.content);
    }

    public getTotalMeasurementSize(measurement: GenericMeasurement): number {
        return measurement.parts.reduce((sum, part) => sum + part.dataSize, 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static getSpecialMeasurementTypeFromData(data: any): SpecialMeasurementType {
        if (data === undefined) throw new Error("Data is undefined");

        if (data.speechLevelReduction != undefined) {
            return SpecialMeasurementType.SLR;
        }
        if (data.OctaveMode != undefined) {
            return SpecialMeasurementType.RT;
        }

        if (data.weightedSoundReductionIndex != undefined) {
            // This is currently the way that handheld determines if it is a STC or SRI measurement
            // Since the data contents are exactly the same
            if (data.fileName.includes("STC")) {
                return SpecialMeasurementType.STC;
            } else {
                return SpecialMeasurementType.SRI;
            }
        }

        throw new Error("Could not determine special measurement type");
    }

    //#endregion
}
